import { useState } from "react";
import { motion } from "framer-motion";
import api from "../lib/api";
import { toast } from "sonner";
import { ArrowRight } from "lucide-react";

const reveal = {
  initial: { opacity: 0, y: 40 }, whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" },
  transition: { duration: 0.9, ease: [0.16, 1, 0.3, 1] },
};

const PROJECTS = ["Brand Film", "Marketing Campaign", "Luxury Photography", "Magazine Feature", "Social Cutdowns", "Creative Direction", "Website / Digital", "Other"];

export default function Contact() {
  const [form, setForm] = useState({
    name: "", email: "", brand_dealership: "", project_type: "Brand Film",
    message: "",
  });
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/contact", form);
      setSent(true);
      toast.success("Thanks. We'll be in touch.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to send.");
    } finally { setBusy(false); }
  };

  return (
    <main data-testid="contact-page" className="bg-black pt-32 pb-24 min-h-screen">
      <section className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-16 grid grid-cols-1 md:grid-cols-12 gap-16 items-start">
        <motion.div {...reveal} className="md:col-span-5">
          <p className="overline mb-6">Inquiries</p>
          <h1 className="font-display text-6xl md:text-7xl text-white leading-[0.95]">
            Let's build something that
            <br /><span className="italic text-[var(--gb-gold)]">moves people.</span>
          </h1>
          <p className="text-white/65 leading-[1.8] mt-8 max-w-md">
            From cinematic brand films to campaigns that drive growth, fundraising
            and community impact — combining creative thinking and marketing to
            deliver lasting results. Have a project in mind? Let's talk.
          </p>
          <div className="gb-divider my-10" />
          <p className="kbd text-white/40">DIRECT</p>
          <p className="mt-3 text-white/80">hello@thegoodbandit.com</p>
        </motion.div>

        <motion.div {...reveal} className="md:col-span-6 md:col-start-7">
          {sent ? (
            <div className="border border-[var(--gb-gold)]/40 p-10 text-center" data-testid="contact-success">
              <p className="overline mb-4">Received</p>
              <h3 className="font-display text-3xl text-white">Thank you. We'll be in touch shortly.</h3>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-7" data-testid="contact-form">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-7">
                <div>
                  <label className="kbd block mb-2">NAME</label>
                  <input data-testid="contact-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="gb-input" />
                </div>
                <div>
                  <label className="kbd block mb-2">EMAIL</label>
                  <input data-testid="contact-email" required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="gb-input" />
                </div>
              </div>
              <div>
                <label className="kbd block mb-2">BRAND</label>
                <input data-testid="contact-brand" value={form.brand_dealership} onChange={(e) => setForm({ ...form, brand_dealership: e.target.value })} className="gb-input" />
              </div>
              <div>
                <label className="kbd block mb-2">PROJECT TYPE</label>
                <select data-testid="contact-project-type" value={form.project_type} onChange={(e) => setForm({ ...form, project_type: e.target.value })} className="gb-input bg-black text-white">
                  {PROJECTS.map((p) => <option key={p} className="bg-black">{p}</option>)}
                </select>
              </div>
              <div>
                <label className="kbd block mb-2">MESSAGE</label>
                <textarea data-testid="contact-message" required value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} rows={5} className="gb-input resize-none" placeholder="Tell us about the project, location, timing…" />
              </div>
              <button data-testid="contact-submit" disabled={busy} className="gb-btn-primary">
                {busy ? "Sending…" : "Send Inquiry"} <ArrowRight size={14} />
              </button>
            </form>
          )}
        </motion.div>
      </section>
    </main>
  );
}
